import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import { head, get } from "lodash"
import moment from "moment"
import { getLoggedInUserDisplayName } from "../v2_actions/auth"
import { getInitials } from "../v2_actions/photographer"
import {
  overviewSalesList, overviewSalesMonth1List, overviewSalesMonth2List, fetchPreviousMonths
} from "../v2_actions/overview_sales"
import { galleryList } from "../v2_actions/gallery"
import { addDaysToDate } from "../v2_actions/time"
import { CurrencyValue } from "./CurrencyValue"
import WhiteLogo from "../assets/images/logo/white_logo.png"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import CollectionsIcon from "@material-ui/icons/Collections"
import FlashOnIcon from "@material-ui/icons/FlashOn"
import CameraAltIcon from "@material-ui/icons/CameraAlt"
import HomeIcon from "@material-ui/icons/Home"
import BarChartIcon from "@material-ui/icons/BarChart"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"

export const MobilePhotographerHome = () => {
    const [statsExpanded, setStatsExpanded] = useState(false)
    const [previousMonthsFetched, setPreviousMonthsFetched] = useState(false)
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const displayName = getLoggedInUserDisplayName()
    const initials = getInitials(displayName)
    const overviewSales = head(useSelector(() => overviewSalesList.getVisibleObjects()))
    const month1Sales = head(useSelector(() => overviewSalesMonth1List.getVisibleObjects()))
    const month2Sales = head(useSelector(() => overviewSalesMonth2List.getVisibleObjects()))
    const recentGalleries = useSelector(() => galleryList.getRecentGalleries())
    const monthsAgoList = [{ monthsAgo: 1, sales: month1Sales }, { monthsAgo: 2, sales: month2Sales }]

    useEffect(() => {
        const startOfMonth = moment().startOf("month").format("YYYY-MM-DD")
        const today = moment().format("YYYY-MM-DD")
        dispatch(overviewSalesList.updateListFilter({
            created_at_date_from: startOfMonth,
            created_at_date_to: addDaysToDate(today)
        }))
        dispatch(overviewSalesList.fetchListIfNeeded())
        dispatch(galleryList.fetchListIfNeeded())
    }, [])

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <img src={WhiteLogo} alt="bossfotos" className={classes.logo} />
                <div className={classes.avatar}>{initials}</div>
            </div>

            <div className={classes.statsSection}>
                <div className={classes.statsContainer}>
                    <div className={classes.statsRow}>
                        <div className={classes.statCard}>
                            <div className={classes.statLabel}>Sales</div>
                            <div className={classes.statValue}>
                                <CurrencyValue
                                    showCents={false}
                                    customStyling={classes.currencyValue}
                                    country={get(overviewSales, "country")}
                                    value={get(overviewSales, "total_amount")}
                                />
                            </div>
                            <div className={classes.statDate}>{moment().format("MMM 'YY")}</div>
                        </div>
                        <div className={classes.statCard}>
                            <div className={classes.statLabel}>Photos Sold</div>
                            <div className={classes.statValue}>{get(overviewSales, "total_photos_sold", 0)}</div>
                            <div className={classes.statDate}>{moment().format("MMM 'YY")}</div>
                        </div>
                    </div>
                    <span
                        className={`${classes.statsChevron} ${statsExpanded ? classes.statsChevronExpanded : ""}`}
                        onClick={() => {
                            dispatch(fetchPreviousMonths(setPreviousMonthsFetched, previousMonthsFetched))
                            setStatsExpanded(!statsExpanded)
                        }}
                    >▼</span>
                </div>
                <div className={`${classes.expandedStats} ${statsExpanded ? classes.expandedStatsVisible : ""}`}>
                    {monthsAgoList.map(({ monthsAgo, sales }) => (
                        <div key={monthsAgo} className={classes.expandedMonthRow}>
                            <div className={classes.expandedMonthLabel}>
                                {moment().subtract(monthsAgo, "months").format("MMM 'YY")}
                            </div>
                            <div className={classes.expandedMonthCards}>
                                <div className={classes.expandedStatCard}>
                                    <div className={classes.expandedStatLabel}>Sales</div>
                                    <div className={classes.expandedStatValue}>
                                        <CurrencyValue
                                            showCents={false}
                                            customStyling={classes.expandedCurrencyValue}
                                            country={get(sales, "country")}
                                            value={get(sales, "total_amount")}
                                        />
                                    </div>
                                </div>
                                <div className={classes.expandedStatCard}>
                                    <div className={classes.expandedStatLabel}>Photos Sold</div>
                                    <div className={classes.expandedStatValue}>{get(sales, "total_photos_sold", 0)}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={classes.quickActionsSection}>
                <div className={classes.quickActionsTitle}>Quick Actions</div>
                <div className={classes.quickActionsRow}>
                    <div className={classes.quickActionButton} onClick={() => history.push("/photographer/mobile-upload")}>
                        <CloudUploadIcon className={classes.quickActionIcon} />
                        <span className={classes.quickActionLabel}>Upload Photos</span>
                    </div>
                    <div className={classes.quickActionButton}>
                        <CollectionsIcon className={classes.quickActionIcon} />
                        <span className={classes.quickActionLabel}>Galleries</span>
                    </div>
                    <div className={classes.quickActionButton}>
                        <FlashOnIcon className={classes.quickActionIcon} />
                        <span className={classes.quickActionLabel}>Run FR</span>
                    </div>
                </div>
            </div>

            <div className={classes.recentGalleriesSection}>
                <div className={classes.recentGalleriesHeader}>
                    <span className={classes.recentGalleriesTitle}>Recent Galleries</span>
                    <span className={classes.recentGalleriesChevron}>›</span>
                </div>
                <div className={classes.galleriesList}>
                    {recentGalleries.map((gallery) => (
                        <div key={gallery.id} className={classes.galleryCard}>
                            <div className={classes.galleryThumbnail}>
                                {gallery.avatar_photo_url ? (
                                    <img
                                        src={gallery.avatar_photo_url}
                                        alt={gallery.session_name}
                                        className={classes.galleryThumbnailImg}
                                    />
                                ) : (
                                    <CameraAltIcon className={classes.galleryThumbnailIcon} />
                                )}
                            </div>
                            <div className={classes.galleryInfo}>
                                <div className={classes.galleryName}>{gallery.session_name}</div>
                                <div className={classes.galleryStats}>
                                    <span className={classes.galleryStatsText}>
                                        {gallery.number_of_photos_in_gallery} photos
                                    </span>
                                </div>
                                <div className={classes.galleryDate}>
                                    {moment(gallery.shot_at_datetime).format("D MMM YYYY")}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className={classes.bottomNav}>
                <div className={classes.navItem}>
                    <HomeIcon className={classes.navIconActive} />
                    <span className={classes.navLabelActive}>Home</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-sales")}>
                    <BarChartIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Sales</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-upload")}>
                    <CloudUploadIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Upload</span>
                </div>
                <div className={classes.navItem}>
                    <MoreHorizIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>More</span>
                </div>
            </div>
        </div>
    )
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        fontFamily: "Helvetica, Raleway, sans-serif",
        paddingBottom: "60px",
    },
    header: {
        background: "linear-gradient(135deg, #0d2137 0%, #1e88e5 100%)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: "36px",
        width: "auto",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#1565c0",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: 600,
        border: "2px solid #fff",
    },
    statsSection: {
        padding: "16px 16px 8px 16px",
    },
    statsContainer: {
        background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
        borderRadius: "16px",
        padding: "16px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    statsRow: {
        display: "flex",
        gap: "12px",
        flex: 1,
    },
    statCard: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "12px 16px",
        flex: 1,
    },
    statLabel: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#1565c0",
        marginBottom: "4px",
    },
    statValue: {
        fontSize: "24px",
        fontWeight: 700,
        color: "#333",
        marginBottom: "2px",
    },
    currencyValue: {
        fontSize: "24px",
        fontWeight: 700,
        color: "#333",
    },
    statDate: {
        fontSize: "12px",
        color: "#999",
    },
    statsChevron: {
        color: "#fff",
        fontSize: "20px",
        cursor: "pointer",
        transition: "transform 0.3s ease",
    },
    statsChevronExpanded: {
        transform: "rotate(180deg)",
    },
    expandedStats: {
        maxHeight: 0,
        overflow: "hidden",
        transition: "max-height 0.35s ease, opacity 0.3s ease",
        opacity: 0,
    },
    expandedStatsVisible: {
        maxHeight: "300px",
        opacity: 1,
    },
    expandedMonthRow: {
        background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
        borderRadius: "12px",
        padding: "12px 16px",
        marginTop: "8px",
    },
    expandedMonthLabel: {
        color: "#fff",
        fontSize: "12px",
        fontWeight: 600,
        marginBottom: "8px",
        opacity: 0.85,
    },
    expandedMonthCards: {
        display: "flex",
        gap: "12px",
    },
    expandedStatCard: {
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "10px 14px",
        flex: 1,
    },
    expandedStatLabel: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#1565c0",
        marginBottom: "2px",
    },
    expandedStatValue: {
        fontSize: "20px",
        fontWeight: 700,
        color: "#333",
    },
    expandedCurrencyValue: {
        fontSize: "20px",
        fontWeight: 700,
        color: "#333",
    },
    quickActionsSection: {
        padding: "6px 16px 8px 16px",
    },
    quickActionsTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        marginBottom: "12px",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
    },
    quickActionsRow: {
        display: "flex",
        justifyContent: "space-between",
    },
    quickActionButton: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "16px 12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        border: "none",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        width: "100px",
    },
    quickActionIcon: {
        color: "#42a5f5",
        fontSize: "28px",
        marginBottom: "8px",
    },
    quickActionLabel: {
        fontSize: "11px",
        color: "#333",
        fontWeight: 500,
        textAlign: "center",
    },
    recentGalleriesSection: {
        padding: "6px 16px 16px 16px",
        flex: 1,
    },
    recentGalleriesHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
    },
    recentGalleriesTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
    },
    recentGalleriesChevron: {
        color: "#42a5f5",
        fontSize: "16px",
        cursor: "pointer",
    },
    galleriesList: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    },
    galleryCard: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    galleryThumbnail: {
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        backgroundColor: "#e0e0e0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    galleryThumbnailImg: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "50%",
    },
    galleryThumbnailIcon: {
        color: "#999",
        fontSize: "24px",
    },
    galleryInfo: {
        flex: 1,
        minWidth: 0,
    },
    galleryName: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "2px",
        lineHeight: 1.3,
    },
    galleryStats: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1px",
        lineHeight: 1.3,
    },
    galleryStatsText: {
        fontSize: "12px",
        color: "#999",
    },
    galleryFrStatus: {
        fontSize: "12px",
        color: "#999",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    galleryFrCheck: {
        fontSize: "14px",
        color: "#4caf50",
    },
    galleryDate: {
        fontSize: "12px",
        color: "#bbb",
        lineHeight: 1.3,
    },
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navIconActive: {
        fontSize: "24px",
        color: "#1e88e5",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
    navLabelActive: {
        fontSize: "10px",
        color: "#1e88e5",
        fontWeight: 600,
    },
})
